import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Pagination from '@mui/material/Pagination';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { URLS } from '../../resources/constants';
import { useTax1099Api } from '../../resources/hooks/api/useTax1099ApiHook';
import { getBranch } from '../../store/user/selectors';
import GenerateFormsModal from './GenerateFormsModal';
import GenerateResultsModal from './GenerateResultsModal';
import FormDetailModal from './FormDetailModal';

/**
 * The 1099 compliance queue: every compiled form for this branch, plus the
 * batch-generate entry point.
 *
 * generate-single-vendor lives on the vendor screen instead of here -- this
 * page is a queue to review, not the place to start a one-off correction.
 */

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const EMPTY_VALUE = '--';

const VENDOR_PICKER_LIMIT = 200;

const currentYear = new Date().getFullYear();
const TAX_YEARS = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];

interface FormRow {
  id: number;
  vendor_id: number;
  recipient_vendor_code?: string | null;
  recipient_legal_name: string;
  tax_year: number;
  form_type: string;
  filing_status: number;
  total_reportable_amount: string;
  federal_tax_withheld: string;
  recipient_is_backup_withholding: boolean;
  generated_at?: string | null;
}

interface VendorOption {
  id: number;
  vendor_code: string;
  display_name: string;
}

const FILING_STATUS_KEYS: Record<number, string> = {
  1: 'status.draft',
  2: 'status.calculated',
  3: 'status.ready_for_review',
  4: 'status.approved',
  5: 'status.void',
};

const FILING_STATUS_BADGE_VARIANT: Record<number, string> = {
  1: 'secondary',
  2: 'secondary',
  3: 'info',
  4: 'success',
  5: 'danger',
};

const money = (value?: string | null) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : EMPTY_VALUE;
};

const Tax1099Forms: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('Tax1099Forms');
  const branchData: any = useSelector(getBranch);
  const branchId = branchData?.id ?? null;

  const {
    fetchFormList,
    fetchFormDetails,
    generateFormsBatch,
    fetchVendorList,
    downloadFormPdf,
    approveForm,
    unapproveForm,
    voidForm,
    furnishFormByEmail,
    correctForm,
  } = useTax1099Api();

  const [rows, setRows] = useState<FormRow[]>([]);
  const [total, setTotal] = useState(0);
  const [savingDetail, setSavingDetail] = useState(false);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [taxYearFilter, setTaxYearFilter] = useState(String(currentYear));
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [formTypeFilter, setFormTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [showGenerate, setShowGenerate] = useState(false);
  const [results, setResults] = useState<{
    taxYear: number; created: any[]; skipped: any[];
  } | null>(null);
  const [detailForm, setDetailForm] = useState<any | null>(null);

  const totalPages = useMemo(
    () => (total > 0 ? Math.ceil(total / pageSize) : 1),
    [total, pageSize],
  );

  useEffect(() => {
    if (!branchId) {
      return;
    }
    fetchVendorList(
      { branch_id: branchId, page: 1, page_size: VENDOR_PICKER_LIMIT, status: 1 },
      (message: string, response: any) => {
        setVendors(response?.data?.data?.results ?? []);
      },
      (message: string) => toast.error(message || t('errors.vendor_load_failed')),
    );
    // eslint-disable-next-line
  }, [branchId]);

  const loadForms = useCallback(() => {
    if (!branchId) {
      return;
    }
    setLoading(true);

    const params: Record<string, any> = {
      branch_id: branchId,
      page,
      page_size: pageSize,
    };
    if (taxYearFilter) {
      params.tax_year = taxYearFilter;
    }
    if (vendorFilter) {
      params.vendor_id = vendorFilter;
    }
    if (statusFilter !== '') {
      params.filing_status = statusFilter;
    }
    if (formTypeFilter) {
      params.form_type = formTypeFilter;
    }

    fetchFormList(
      params,
      (message: string, response: any) => {
        const data = response?.data?.data ?? {};
        setRows(data.results ?? []);
        setTotal(data.count ?? 0);
        setLoading(false);
      },
      (message: string) => {
        setLoading(false);
        toast.error(message || t('errors.load_failed'));
      },
    );
    // eslint-disable-next-line
  }, [branchId, page, pageSize, taxYearFilter, vendorFilter, statusFilter, formTypeFilter]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  useEffect(() => {
    setPage(1);
  }, [taxYearFilter, vendorFilter, statusFilter, formTypeFilter, pageSize]);

  const handleGenerate = (taxYear: number, vendorIds: number[]) => {
    if (!branchId) {
      return;
    }
    setGenerating(true);
    generateFormsBatch(
      { branch_id: branchId, tax_year: taxYear, vendor_ids: vendorIds.length ? vendorIds : undefined },
      (message: string, response: any) => {
        setGenerating(false);
        setShowGenerate(false);
        const data = response?.data?.data ?? {};
        setResults({
          taxYear: data.tax_year ?? taxYear,
          created: data.created ?? [],
          skipped: data.skipped ?? [],
        });
        loadForms();
      },
      (message: string) => {
        setGenerating(false);
        toast.error(message || t('generate.failed'));
      },
    );
  };

  const openDetail = (formId: number) => {
    if (!branchId) {
      return;
    }
    fetchFormDetails(
      { form_id: formId, branch_id: branchId },
      (message: string, response: any) => {
        setDetailForm(response?.data?.data ?? null);
      },
      (message: string) => toast.error(message || t('errors.load_failed')),
    );
  };

  const runStatusTransition = (
    action: (data: any, onSuccess: Function, onError: Function) => void,
    successKey: string,
    errorKey: string,
  ) => {
    if (!branchId || !detailForm) {
      return;
    }
    setSavingDetail(true);
    action(
      { form_id: detailForm.id, branch_id: branchId },
      (message: string) => {
        setSavingDetail(false);
        toast.success(message || t(successKey));
        openDetail(detailForm.id);
        loadForms();
      },
      (message: string) => {
        setSavingDetail(false);
        toast.error(message || t(errorKey));
      },
    );
  };

  const handleApprove = () =>
    runStatusTransition(approveForm, 'detail.approved', 'detail.approve_failed');
  const handleUnapprove = () =>
    runStatusTransition(unapproveForm, 'detail.unapproved', 'detail.unapprove_failed');
  const handleVoid = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('detail.void_confirm'))) {
      return;
    }
    runStatusTransition(voidForm, 'detail.voided', 'detail.void_failed');
  };
  const handleFurnishEmail = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('detail.furnish_email_confirm'))) {
      return;
    }
    runStatusTransition(furnishFormByEmail, 'detail.furnished', 'detail.furnish_email_failed');
  };
  const handleCorrect = (correctionType: 1 | 2) => {
    if (!branchId || !detailForm) {
      return;
    }
    const confirmKey = correctionType === 1
      ? 'detail.correct_amount_confirm'
      : 'detail.correct_identity_confirm';
    // eslint-disable-next-line no-alert
    if (!window.confirm(t(confirmKey))) {
      return;
    }
    setSavingDetail(true);
    correctForm(
      { form_id: detailForm.id, branch_id: branchId, correction_type: correctionType },
      (message: string, response: any) => {
        setSavingDetail(false);
        const created = response?.data?.data?.created ?? [];
        toast.success(t('detail.corrected', { count: created.length }));
        openDetail(detailForm.id);
        loadForms();
      },
      (message: string) => {
        setSavingDetail(false);
        toast.error(message || t('detail.correct_failed'));
      },
    );
  };

  const handleDownloadPdf = (form: { id: number; form_type: string; tax_year: number }) => {
    if (!branchId) {
      return;
    }
    downloadFormPdf(
      { form_id: form.id, branch_id: branchId },
      (message: string, blob: Blob) => {
        // Same blob-to-download pattern used for the Excel/CSV exports
        // elsewhere in gl-ui -- CallApi.DOWNLOAD already set responseType
        // 'blob' and the auth header, so this just turns it into a save.
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${form.form_type.replace('/', '-')}_${form.tax_year}_form_${form.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      (message: string) => toast.error(message || t('errors.pdf_failed')),
    );
  };

  if (!branchId) {
    return (
      <div className="p-4">
        <h4>{t('page.title')}</h4>
        <p className="text-muted">{t('messages.select_branch')}</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0">{t('page.title')}</h4>
          <small className="text-muted">{t('page.subtitle')}</small>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-primary"
            style={{
              borderColor: '#14509e',
              color: '#14509e',
              backgroundColor: '#fff',
              fontWeight: 700,
            }}
            onClick={() => navigate(URLS.TAX_1099_PORTFOLIO)}
          >
            🏢 {t('actions.firm_portfolio', 'Firm Portfolio')}
          </Button>
          <Button
            style={{ backgroundColor: '#14509e', borderColor: '#14509e', color: '#fff', fontWeight: 700 }}
            onClick={() => setShowGenerate(true)}
          >
            {t('actions.generate')}
          </Button>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-2 mb-2">
          <Form.Select value={taxYearFilter} onChange={(event) => setTaxYearFilter(event.target.value)}>
            <option value="">{t('filter.all_years')}</option>
            {TAX_YEARS.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-3 mb-2">
          <Form.Select value={vendorFilter} onChange={(event) => setVendorFilter(event.target.value)}>
            <option value="">{t('filter.all_vendors')}</option>
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.vendor_code} — {vendor.display_name}
              </option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-2 mb-2">
          <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="">{t('filter.all_statuses')}</option>
            {Object.entries(FILING_STATUS_KEYS).map(([value, key]) => (
              <option key={value} value={value}>{t(key)}</option>
            ))}
          </Form.Select>
        </div>
        <div className="col-md-2 mb-2">
          <Form.Select value={formTypeFilter} onChange={(event) => setFormTypeFilter(event.target.value)}>
            <option value="">{t('filter.all_form_types')}</option>
            <option value="1099-NEC">1099-NEC</option>
            <option value="1099-MISC">1099-MISC</option>
          </Form.Select>
        </div>
        <div className="col-md-1 mb-2">
          {(taxYearFilter || vendorFilter || statusFilter || formTypeFilter) && (
            <Button
              variant="outline-secondary"
              className="w-100"
              onClick={() => {
                setTaxYearFilter('');
                setVendorFilter('');
                setStatusFilter('');
                setFormTypeFilter('');
              }}
              title={t('filter.clear', 'Clear filters')}
            >
              ✕
            </Button>
          )}
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th>{t('table.headers.vendor')}</th>
              <th>{t('table.headers.tax_year')}</th>
              <th>{t('table.headers.form_type')}</th>
              <th className="text-end">{t('table.headers.total')}</th>
              <th className="text-end">{t('table.headers.federal_withheld')}</th>
              <th>{t('table.headers.status')}</th>
              <th>{t('table.headers.backup_withholding')}</th>
              <th className="text-end">{t('table.headers.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">
                  {t('messages.loading')}
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">
                  {t('messages.no_forms')}
                </td>
              </tr>
            )}

            {!loading && rows.map((row) => (
              <tr key={row.id}>
                <td>
                  {row.recipient_legal_name}
                  <div className="text-muted small">
                    <code>{row.recipient_vendor_code}</code>
                  </div>
                </td>
                <td>{row.tax_year}</td>
                <td>{row.form_type}</td>
                <td className="text-end">{money(row.total_reportable_amount)}</td>
                <td className="text-end">{money(row.federal_tax_withheld)}</td>
                <td>
                  <Badge bg={FILING_STATUS_BADGE_VARIANT[row.filing_status] || 'secondary'}>
                    {t(FILING_STATUS_KEYS[row.filing_status] || 'status.draft')}
                  </Badge>
                </td>
                <td>
                  {row.recipient_is_backup_withholding
                    ? <Badge bg="warning" text="dark">{t('table.yes')}</Badge>
                    : EMPTY_VALUE}
                </td>
                <td className="text-end">
                  <Button variant="link" size="sm" onClick={() => openDetail(row.id)}>
                    {t('actions.view')}
                  </Button>
                  <Button variant="link" size="sm" onClick={() => handleDownloadPdf(row)}>
                    {t('actions.pdf')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">{t('pagination.rows_per_page')}</span>
          <Form.Select
            size="sm"
            style={{ width: 90 }}
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </Form.Select>
          <span className="text-muted small">
            {t('pagination.total', { count: total })}
          </span>
        </div>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_event, value) => setPage(value)}
          size="small"
        />
      </div>

      <GenerateFormsModal
        show={showGenerate}
        saving={generating}
        vendors={vendors}
        onClose={() => setShowGenerate(false)}
        onGenerate={handleGenerate}
      />

      <GenerateResultsModal
        show={Boolean(results)}
        taxYear={results?.taxYear ?? null}
        created={results?.created ?? []}
        skipped={results?.skipped ?? []}
        onClose={() => setResults(null)}
      />

      <FormDetailModal
        show={Boolean(detailForm)}
        form={detailForm}
        saving={savingDetail}
        onClose={() => setDetailForm(null)}
        onDownloadPdf={() => detailForm && handleDownloadPdf(detailForm)}
        onApprove={handleApprove}
        onUnapprove={handleUnapprove}
        onVoid={handleVoid}
        onFurnishEmail={handleFurnishEmail}
        onCorrect={handleCorrect}
      />
    </div>
  );
};

export default Tax1099Forms;
